<template>
    <div class="betSlip comboParlay notranslate">
        <div class="betSlip-area">
            <div class="betSlip-title">
                <span v-on:click="hideBetSlip">
                    <SvgIcon iconName="systemIcon_back" />
                </span>

                <div class="betSlip-title-name">
                    {{ mpTitle }}
                </div>
                <div class="betSlip-title-mixParlay-removeAll ParlaySlip_Btn_RemoveAll" v-on:click="removeBetSlip">
                    <SvgIcon iconName="systemIcon_removeAll" />
                </div>
            </div>
            <div
                class="betSlip-scrollContent"
                v-bind:class="{ _stakePadShow: !isShowStakePad }"
            >
                <div class="betSlip-ticketArea">
                    <TransitionGroup name="list-complete">
                        <div
                            v-for="(single, key) in betSlip.tickets"
                            v-bind:key="key"
                            class="ticket-container list-complete-item"
                        >
                            <EuroSingleTicket
                                v-bind:singleTicket="single"
                                v-bind:isMixParlay="true"
                                v-on:refreshTicket="refresh(false, true)"
                                v-on:removeTicket="removeTicketOrBetSlip(key)"
                            />
                            <Message
                                v-if="single.message.key"
                                status="error"
                                v-bind:messageKey="single.message.key"
                                v-bind:messageData="single.message.data"
                                class="ticket-error-message"
                            />
                        </div>
                    </TransitionGroup>

                    <TransitionGroup name="fade">
                        <div
                            v-for="n in countOfAddMore"
                            v-bind:key="n"
                            class="betSlip-addMore betSlip-addMoreMp"
                            v-on:click="hideBetSlip"
                        >
                            <ParlayTicketCountInsufficientMessage v-if="n === 1" />
                            <span v-else>+</span>
                        </div>
                    </TransitionGroup>
                    <div
                        v-if="isValidSubBetCount && ticketCount < 11"
                        class="betSlip-addMore betSlip-addMoreMp"
                        v-on:click="hideBetSlip"
                    >
                        <span>+</span>
                    </div>

                    <div v-if="isValidSubBetCount" class="betSlip-info safeAreaBottom">
                        <div class="betSlip-info-totalOdds">
                            <span>{{ i18n.t('betSlip_totalBets') }}:</span>
                            <span>{{ totalBetCount }}</span>
                        </div>
                        <div class="betSlip-info-totalOdds">
                            <span>{{ i18n.t('betSlip_totalStake') }}:</span>
                            <span>
                                {{ totalParlayStakeDisplay }}
                            </span>
                        </div>
                        <div class="betSlip-info-maxPayout">
                            <span>{{ i18n.t('maxPayout') }}:</span>
                            <span class="notranslate betSlip-info-maxPayout-amount">
                                {{ totalPayOutWithComboParlay }}
                            </span>
                        </div>
                        <PlaceBet
                            class="combo-placeBet"
                            v-bind:stake="stake"
                            v-bind:isDisableBtn="isDisablePlaceBet"
                            v-bind:balance="balance"
                            v-bind:minBet="betSlip.min"
                            v-bind:price="price"
                            v-on:placeBet="placeBet"
                            v-on:changeStake="setStake"
                        />
                        <StakePad
                            v-bind:stake="stake"
                            v-bind:balance="balance"
                            v-bind:minBet="betSlip.min"
                            v-bind:maxBet="betSlip.max"
                            v-bind:currency="customerInfo.currency"
                            v-on:changeStake="setStake"
                        />
                        <SwitchAcceptAnyOddsValue
                            v-if="!customerInfo.isAcceptAnyOdds"
                            class="combo-acceptAnyOdds"
                        />
                    </div>
                </div>
                <div v-if="countOfAddMore === 0" class="combo">
                    <h2 class="combo-title">
                        {{ comboParlayOptionTitle }}
                        <span
                            v-if="!isValid2MpSubBets"
                            class="combo-info"
                            v-on:click="showComboInfo = !showComboInfo"
                        >
                            i
                        </span>
                    </h2>
                    <div
                        v-for="(option, index) in comboParlayOptions"
                        v-bind:key="index"
                        class="combo-item"
                        v-bind:class="{ _selected: option.selected }"
                        v-on:click="addOrRemoveSelectedCombo(option.key)"
                    >
                        <span class="combo-item-checkbox">
                            <SvgIcon iconName="systemIcon_checked" />
                        </span>
                        <div>
                            {{ option.option }}
                            <SvgIcon
                                v-if="option.canCashOut"
                                class="combo-item-cash-out"
                                iconName="sportIcon_cashOut"
                            />
                            <span v-if="option.betCount > 1">({{ i18n.t('betSlip_n_bets', { n: option.betCount }) }})</span>
                            <span v-else class="combo-odds">@{{ toFixedDecimalPoint(option.odds, 3) }}</span>
                        </div>
                        <div class="combo-payout">
                            <p>{{ i18n.t('payout') }}</p>
                            {{ formatMoneyWithDecimal(option.payout) }}
                        </div>
                    </div>
                </div>

                <ComboParlayInfoPopup v-if="showComboInfo" v-on:close="showComboInfo = false" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import EuroSingleTicket from './EuroSingleTicket.vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import ComboParlayInfoPopup from '@/components/betSlip/ComboParlayInfoPopup.vue';
    import ParlayTicketCountInsufficientMessage from '@/components/betSlip/ParlayTicketCountInsufficientMessage.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import type { IBetSlipCashOut } from '@/interface/IBetSlipCashOut';
    import { RoutePage } from '@/interface/enum';
    import { useParlayCashOut } from '@/libraries/composables/useBetSlipCashOut';
    import { isValidComboBet } from '@/libraries/helpers/comboParlayHelper';
    import { i18n } from '@/libraries/i18n';
    import { isMainMarket } from '@/libraries/marketGroupHelper';
    import { enableScrolling } from '@/libraries/utils/dom';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const ticks = ref(0);
    const showComboInfo = ref(false);

    const accountStore = useAccountStore();
    const { customerInfo, balance } = storeToRefs(accountStore);
    const { formatMoneyWithDecimal } = accountStore;

    const { cashOutSubBetMinCount } = storeToRefs(useFeatureToggleStore());

    const {
        showMiniBetSlip,
        setIsShowStakePad,
    } = useBetSlipStore();

    const { isShowStakePad } = storeToRefs(useBetSlipStore());

    const {
        openTicket,
        placeBet,
        resetSelectedCombo,
        removeBetSlip,
        removeTicketOrBetSlip,
        setStake,
    } = useParlayBetSlipStore();

    const {
        betSlip,
        isPlacingBet,
        ticketCount,
        totalBetCount,
        comboTotalOdds,
        isValid2MpSubBets,
        isValidSubBetCount,
        comboOdds,
        countOfAddMore,
        price,
        stake,
        totalParlayStakeDisplay,
        subBets,
        totalPayOutWithComboParlay,
    } = storeToRefs(useParlayBetSlipStore());

    const mpTitle = computed(() => i18n.t('bottom_function_bet_slip'));

    const subBetCashOuts = computed<IBetSlipCashOut[]>(() => subBets.value.map(bet => ({
        sportType: bet.sportType,
        isMainMarket: isMainMarket(bet.marketGroup.id),
        isLive: bet.isLive,
        leagueId: bet.leagueId,
        marketType: bet.marketType,
    })));
    const { isDisplayCashOutIcon: allTicketSupportCashOut } = useParlayCashOut(subBetCashOuts);

    const comboParlayOptions = computed(() => {
        if (ticketCount.value > 8) {
            const comboTotalOdd = comboTotalOdds.value[ticketCount.value];

            return [{
                option: i18n.t('betSlip_comboParlay_option_n', { n: ticketCount.value }),
                odds: comboTotalOdd,
                betCount: comboOdds.value[ticketCount.value].length,
                payout: comboTotalOdd * stake.value,
                key: ticketCount.value,
                selected: betSlip.value.selectedCombo.indexOf(ticketCount.value) > -1,
                canCashOut: allTicketSupportCashOut.value,
            }];
        }
        const result = Array.from({ length: ticketCount.value - 1 }, (_, i) => {
            const index = i + 2;
            const comboTotalOdd = comboTotalOdds.value[index];

            return {
                option: index <= 3
                    ? i18n.t(`betSlip_comboParlay_option_${index}`)
                    : i18n.t('betSlip_comboParlay_option_n', { n: index }),
                odds: comboTotalOdd,
                betCount: comboOdds.value[index].length,
                payout: comboTotalOdd * stake.value,
                key: index,
                selected: betSlip.value.selectedCombo.includes(index),
                canCashOut: allTicketSupportCashOut.value && index >= cashOutSubBetMinCount.value,
            };
        });
        return result;
    });

    const comboParlayOptionTitle = computed(() => i18n.t('betSlip_comboParlay'));

    const isDisablePlaceBet = computed(() => {
        if (isPlacingBet.value) return true;
        if (!isValidComboBet(betSlip.value, isValidSubBetCount.value)) return true;
        if (balance.value === null) return false;
        if (balance.value < betSlip.value.min) return true;
        return Math.floor(balance.value) <= 0;
    });

    function scrollToBottom() {
        const scrollDiv = document.getElementsByClassName('betSlip-scrollContent')[0];
        scrollDiv?.scrollTo(0, scrollDiv.scrollHeight);
    }

    async function tick() {
        ticks.value++;
        if (ticks.value >= 10) {
            await refresh(false, false);
        }
    }

    async function refresh(isFirstTime: boolean, force: boolean) {
        ticks.value = 0;
        if (force) {
            await openTicket(isFirstTime);
        }
    }

    const route = useRoute();
    const router = useRouter();

    function hideBetSlip() {
        showMiniBetSlip();
        resetSelectedCombo();
        if (route.name !== RoutePage.Main && route.name !== RoutePage.MainBySport) {
            router.push({ name: RoutePage.Main });
        }
    }

    function addOrRemoveSelectedCombo(key: number) {
        const index = betSlip.value.selectedCombo.indexOf(key);

        if (index > -1) {
            betSlip.value.selectedCombo.splice(index, 1);
        } else {
            betSlip.value.selectedCombo.push(key);
        }
    }

    onMounted(async () => {
        scrollToBottom();
        window.eventEmitter.on('tick', tick);
        await refresh(true, true);
    });

    setIsShowStakePad(true);

    onBeforeUnmount(() => {
        window.eventEmitter.off('tick', tick);
        enableScrolling();
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/betSlipComboParlay";
</style>
